import React, { useState, useEffect } from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Typography,
  IconButton,
  ButtonGroup,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Divider,
  Breadcrumbs,
} from '@material-ui/core';
import Axios from 'axios';
import { GetApp, ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import AdminLayout from '../../../../../../../../layouts/AdminLayout';
import Spec from '../../../../../../../../models/Spec';
import APITools from '../../../../../../../../models/APITools';
import { GradeInput } from '../../../../../../../../components/Inputs';
import Submission, { Log } from '../../../../../../../../models/Submission';
import StudentSubmission, {
  DriveFile,
  MoodleFile
} from '../../../../../../../../models/StudentSubmission';
import utils from '../../../../../../../../libs/utils';
import useSolutionGallery from '../../../../../../../../hooks/useSolutionGallery';
import useZipFileExplorerPopUp from '../../../../../../../../hooks/useZipFileExplorerPopUp';
import useSpecComments from '../../../../../../../../hooks/useSpecComments';
import CourseWork from '../../../../../../../../models/CourseWork';
import Student from '../../../../../../../../models/Student';
import {
  SpecTest,
  TestResult,
} from '../../../../../../../../components/Spec/SpecTest';
import Course from '../../../../../../../../models/Course';
import { ClassroomResourceLink } from '../../../../../../../../components/Links';
import { TitleHeader } from '../../../../../../../../components/Headers';
import auth from '../../../../../../../../libs/auth';

interface WorkItem {
  spec: Spec;
  submission: {
    file: MoodleFile;
    content: string;
  } | null;
  answer: string;
  rawFileAnswer?: File;
  testResult: TestResult | null;
  manual: boolean;
  uploadStatus?: {
    isLoading: boolean;
    errorMessage: string;
    successMessage: string;
  };
}
const useStyles = makeStyles((theme) => ({
  accordion: {
    overflow: 'hidden',
    color: 'white',
  },
  accordionSummary: {
    backgroundColor: 'rgb(55, 71, 79)',
    color: 'white',
    paddingLeft: 0,
  },
}));

const AssignmentProfile = (
  props: RouteComponentProps<{
    course_id: string;
    assignment_id: string;
    student_id: string;
  }>
) => {
  const {
    course_id: courseId,
    assignment_id: courseWorkId,
    student_id: studentId,
  } = props.match.params;

  const [course, setCourse] = useState<Course | null>(null);
  const [courseWork, setCourseWork] = useState<CourseWork | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [student, setStudent] = useState<Student | null>(null);
  const [studentName, setStudentName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [overridden, setOverridden] = useState<boolean>(false);
  const [submissionLogs, setSubmissionLogs] = useState<Log[]>([]);
  const [rawSubmittedFiles, setRawSubmittedFiles] = useState<DriveFile[]>([]);
  const [studentSubmission, setStudentSubmission] =
    useState<StudentSubmission | null>(null);

  const hasNewSubmission = (
    courseID: string,
    studentID: string,
    assignmentID: string,
    specID: string
  ): number => {
    let arr: Log[] = submissionLogs.filter(
      (log: Log): Boolean =>
        log.courseID === courseID &&
        log.personID === studentID &&
        log.assignmentID === assignmentID &&
        log.specID === specID
    );
    return arr.length > 0 ? arr[0].submissionID : 0;
  };

  const getGradeStatus = async () => {
    const params = {
      accountID: studentId,
      assignmentID: courseWorkId,
    };
    const queryString = APITools.parseQueryParams(params);
    const { data } = await Axios.get(
      `/admin/grade-override-status${queryString}`
    );
    setOverridden(data);
  };

  const loadLogs = async () => {
    const data = await Submission.getSubmissionLogs();
    setSubmissionLogs(data);
  };

  const loadCourse = async () => {
    const _course = await Course.findById({
      courseId,
    });

    setCourse(_course);
  };

  const loadWorkItems = async () => {
    const specs = await Spec.find({
      assignmentID: courseWorkId,
    });

    const _workItems: WorkItem[] = specs.map((spec) => {
      return {
        spec,
        submission: null,
        answer: '',
        testResult: null,
        manual: !spec.data.length,
      };
    });

    setWorkItems(_workItems);

    const submissionResponse = await CourseWork.getSubmission({
      assignmentIds: [courseWorkId],
      userId: parseInt(studentId),
    });

    const submission = submissionResponse?.data;

    if (submission) {
      const submissions = _workItems.map((item, index) => {
        const matchingSubmission = submission.find(
          (sub: { file: { title: string } }) =>
            sub.file.title.split('.')[0] === item.spec.name
        );

        if (matchingSubmission) {
          const content = matchingSubmission.file.content;
          const title = matchingSubmission.file.title as string;

          if (title.endsWith('.zip')) {
            const binaryString = atob(content);

            const uint8Array = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              uint8Array[i] = binaryString.charCodeAt(i);
            }

            const zipBlob = new Blob([uint8Array], {
              type: 'application/zip',
            });
            const zipFile = new File([zipBlob], item.spec.name + '.zip', {
              type: 'application/zip',
            });

            const rawFileAnswer = zipFile;
            const alternateLink = URL.createObjectURL(zipFile);

            return {
              ...item,
              submission: {
                file: {
                  title,
                  id: '',
                  alternateLink,
                },
                content,
              },
              rawFileAnswer,
            };
          }

          return {
            ...item,
            submission: {
              file: {
                title,
                id: '',
                alternateLink: '',
              },
              content,
            },
            answer: content,
          };
        }

        return item;
      });
      
      setWorkItems(submissions);
    }
  };

  const loadCourseWork = async () => {
    const session = auth.getSession();

    const moodleCourseAssignments = await CourseWork.getMoodleCourseAssignments(
      {
        token: session?.accessToken,
        courseId,
      }
    );

    if (!moodleCourseAssignments)
      throw new Error('Unable to get coursework profile');

    const _courseWork = moodleCourseAssignments.find(
      (obj) => obj.id === courseWorkId
    ) as CourseWork;
    setCourseWork(_courseWork);
  };

  const loadStudent = async () => {
    const _student = await Student.findOne({
      courseId: courseId,
      userId: studentId,
    });
    if (!_student) throw new Error('Unable to get student profile');
    setStudent(_student);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      await Promise.all([
        loadCourse(),
        loadWorkItems(),
        loadCourseWork(),
        // loadStudent(),
        loadLogs(),
        // getGradeStatus()
      ]);
    } catch (error) {
      setErrorMessage('Error loading: ' + error.message);
    } finally {
      setIsLoading(false);
      window.scrollTo(0, 0);
    }
  };

  const classes = useStyles();

  const { renderModal, renderOpenButton, renderSaveButton, renderSnackbar } =
    useSolutionGallery();

  const {
    renderModal: renderFileExplorerModal,
    renderOpenLink: renderFileExplorerOpenLink,
    renderOpenButton: renderFileExplorerOpenButton,
  } = useZipFileExplorerPopUp();

  const {
    renderModal: renderSpecCommentsModal,
    renderOpenButton: renderSpecCommentsOpenButton,
  } = useSpecComments();

  const downloadDriveAssignment = async (fileId: string, studentId: string) => {
    const params = { fileId, studentId };
    const queryString = APITools.parseQueryParams(params);
    const { data } = await Axios.get(
      `/classroom/courses/course-work/student-submission/file${queryString}`
    );
    const { fileBinary, fileType } = data;

    const arr = new Uint8Array(Buffer.from(fileBinary, 'hex'));
    const blob = new Blob([arr], { type: fileType });
    return blob;
  };

  const getTotalTestScore = (): number | null => {
    const items = workItems.filter((a) => !!a.testResult);
    if (items.length) {
      let score = 0;
      for (let item of items)
        if (item.testResult)
          score +=
            item.testResult.output.filter((a) => a.passed).length /
            item.testResult.output.length;
      return score;
    }
    return null;
  };

  const markAllAsCorrectHandler = (workItemIndex: number) => {
    const workItem = workItems[workItemIndex];
    setWorkItems(
      workItems.map((item) => {
        if (item === workItem && item.testResult) {
          for (let outputItem of item.testResult.output)
            outputItem.passed = true;
        }
        return item;
      })
    );
  };

  const markAsCorrectHandler = (workItemIndex: number, outputIndex: number) => {
    const workItem = workItems[workItemIndex];
    setWorkItems(
      workItems.map((item) => {
        if (item === workItem && item.testResult) {
          const output = item.testResult.output[outputIndex];
          if (output) output.passed = true;
        }
        return item;
      })
    );
  };

  const testScore = getTotalTestScore();

  useEffect(() => {
    (async () => await loadData())();
  }, []);

  return (
    <AdminLayout>
      {renderModal()}
      {renderSnackbar()}
      {renderFileExplorerModal()}
      {renderSpecCommentsModal()}

      <div className="py-4">
        <TitleHeader
          breadcrumb={
            <Breadcrumbs aria-label="breadcrumb">
              <Link to={Course.getListUrlPath('admin')}>Courses</Link>
              <Typography>{course?.fullname || courseId}</Typography>
              <Link to={CourseWork.getListUrlPath(courseId, 'admin')}>
                Assignments
              </Link>
              <Typography>{courseWork?.name || courseWorkId}</Typography>
            </Breadcrumbs>
          }
        >
          <Typography variant="h5">
            <span>{courseWork?.name || courseWorkId} </span>
            {!!courseWork && <ClassroomResourceLink resource={courseWork} />}
          </Typography>
        </TitleHeader>

        {(() => {
          // loading data
          if (isLoading)
            return (
              <div className="text-center p-5">
                <CircularProgress />
                <p className="m-0">Loading Data</p>
              </div>
            );

          // loading data error
          if (errorMessage)
            return (
              <div className="text-center p-5">
                <h2 className="m-0">Error Loading Data</h2>
                <p>{errorMessage}</p>
                <Button
                  type="button"
                  onClick={loadData}
                  color="primary"
                  className="mt-3"
                  variant="contained"
                >
                  Try Again
                </Button>
              </div>
            );

          if (courseWork)
            return (
              <>
                <Box py={1}>
                  <Divider />
                </Box>

                <Grid container alignItems="center" className="flex-fill">
                  <Grid item xs={6}>
                    <b>Student: &nbsp;</b>
                    <span>{studentName || studentId}</span>
                  </Grid>
                  {!!studentSubmission && (
                    <Grid item container xs={6} spacing={1} alignItems="center">
                      <Grid item>
                        <b>{overridden ? 'Overridden Grade' : 'Grade'}:</b>
                      </Grid>
                      <Grid item>
                        <GradeInput
                          courseWork={courseWork}
                          submission={studentSubmission}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>

                {testScore !== null && (
                  <Box pb={1}>
                    <Typography variant="h5">
                      Test Score: {utils.formatScore(testScore)} /{' '}
                      {workItems.length} (
                      {utils.formatScore((testScore / workItems.length) * 100)}
                      %)
                    </Typography>
                  </Box>
                )}

                {!!rawSubmittedFiles.length && (
                  <div className="mt-4 mb-3">
                    <Accordion className={classes.accordion}>
                      <AccordionSummary
                        className={classes.accordionSummary}
                        expandIcon={
                          <ExpandMoreIcon
                            style={{ color: 'white' }}
                            color="inherit"
                          />
                        }
                      >
                        <h4 className="m-0 pl-3">Raw Submitted Files</h4>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table aria-label="simple table">
                          <TableHead>
                            <TableRow>
                              <TableCell component="th">
                                <Typography variant="h6">Filename</Typography>
                              </TableCell>
                              <TableCell align="right" />
                              <TableCell align="right" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rawSubmittedFiles.map(
                              (item) =>
                                !!item.title && (
                                  <TableRow key={item.id}>
                                    <TableCell>{item.title}</TableCell>

                                    <TableCell align="right">
                                      {/\.zip/gi.test(item.title) &&
                                        renderFileExplorerOpenLink({
                                          fileId: item.id,
                                          params: { studentId },
                                        })}
                                    </TableCell>

                                    <TableCell align="right">
                                      <a
                                        href={item.alternateLink}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Download
                                      </a>
                                    </TableCell>
                                  </TableRow>
                                )
                            )}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {workItems.map((workItem, index) => {
                  const perfectScore = workItem.testResult
                    ? workItem.testResult.output.length
                    : 0;
                  const score = workItem.testResult
                    ? workItem.testResult.output.filter((a) => a.passed).length
                    : 0;
                  const logID = hasNewSubmission(
                    courseId,
                    studentId,
                    courseWorkId,
                    workItem.spec.id
                  );

                  return (
                    <Paper
                      key={workItem.spec.id}
                      className="mt-4"
                      style={{ overflow: 'hidden' }}
                    >
                      <div
                        className="px-3 py-2 d-flex justify-content-between align-items-center"
                        style={{ backgroundColor: '#37474f', color: 'white' }}
                      >
                        <div className="d-flex">
                          <h3 className="m-0">{workItem.spec.name}</h3>
                          {logID ? (
                            <p style={{ color: '#2196F3' }}>&#9679;</p>
                          ) : null}
                        </div>

                        {!!workItem.testResult && (
                          <div>
                            Score: {score} / {perfectScore}
                          </div>
                        )}
                      </div>

                      <div className="p-3">
                        {(() => {
                          if (workItem.manual)
                            return (
                              <div className="text-center">
                                {(() => {
                                  if (workItem.submission)
                                    return (
                                      <div>
                                        <IconButton
                                          component="a"
                                          href={
                                            workItem.submission.file
                                              .alternateLink
                                          }
                                          target="_blank"
                                          download={workItem.submission.file.title}
                                        >
                                          <GetApp fontSize="large" />
                                        </IconButton>
                                        <p>{workItem.submission.file.title}</p>

                                        {/\.zip/gi.test(
                                          workItem.submission.file.title
                                        ) && (
                                          <div className="pt-4">
                                            {renderFileExplorerOpenButton({
                                              fileId:
                                                workItem.submission.file.id,
                                              params: { studentId },
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );

                                  return <p>No submitted file yet.</p>;
                                })()}
                              </div>
                            );

                          return (
                            <>
                              {!workItem.submission && (
                                <Typography variant="body1" color="error">
                                  No submission detected.
                                </Typography>
                              )}

                              <SpecTest
                                key={workItem.spec.id}
                                courseId={courseId}
                                courseWorkId={courseWorkId}
                                spec={workItem.spec}
                                answer={workItem.answer}
                                onAnswerChange={(answer: string) => {
                                  setWorkItems(
                                    workItems.map((item) => {
                                      if (item === workItem)
                                        item.answer = answer;
                                      return item;
                                    })
                                  );
                                }}
                                onTestResultChange={(
                                  result: TestResult | null
                                ) => {
                                  setWorkItems(
                                    workItems.map((item) => {
                                      if (item === workItem)
                                        item.testResult = result;
                                      return item;
                                    })
                                  );
                                }}
                                editorFooter={
                                  <ButtonGroup>
                                    {renderSaveButton({
                                      specId: workItem.spec.id,
                                      code: workItem.answer,
                                    })}
                                    {renderOpenButton({
                                      specId: workItem.spec.id,
                                    })}
                                    {renderSpecCommentsOpenButton({
                                      specId: workItem.spec.id,
                                    })}
                                  </ButtonGroup>
                                }
                                activityIndex={index}
                                onMarkAllAsCorrect={markAllAsCorrectHandler}
                                onMarkAsCorrect={markAsCorrectHandler}
                              />
                            </>
                          );
                        })()}
                      </div>
                    </Paper>
                  );
                })}
              </>
            );

          return '';
        })()}
      </div>
    </AdminLayout>
  );
};

export default AssignmentProfile;
